import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FileText,
  CheckCircle2,
  Clock,
  Sparkles,
  ArrowRight,
  Loader2,
  AlertTriangle,
  BadgeCheck,
  Award,
  X,
  Quote,
  ShieldCheck,
  Download,
  BookOpen,
  Lightbulb,
  Target,
  TrendingUp,
  RefreshCw,
  XCircle,
  Trophy,
  GraduationCap,
  Zap,
  ChevronRight,
  History,
  Library,
  MessageCircle,
  Mail,
} from "lucide-react";
import AppShell from "../AppShell";
import { api, API, getToken } from "../api";
import { useAuth } from "../AuthContext";
import PremiumCertificate from "../../components/site/PremiumCertificate";
import ScaledCertificate from "../../components/certificate/ScaledCertificate";
import { exportCertificatePDF } from "../pdf";
import {
  PageHeader,
  ErrorBanner,
  EmptyState,
  LoadingRow,
} from "../../components/primitives";

/* ------------ helpers ------------ */

function relativeDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function StatusPill({ status }) {
  const map = {
    pending: { label: "To do", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    in_progress: { label: "In progress", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    superseded: { label: "Superseded", cls: "bg-slate-100 text-slate-600 border-slate-200" },
    assigned: { label: "To do", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  };
  const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return (
    <span className={`text-[10.5px] uppercase tracking-[0.14em] font-semibold border rounded px-2 py-0.5 ${s.cls}`}>
      {s.label}
    </span>
  );
}

/* ------------ Page ------------ */

export default function MyTraining() {
  const { user, business } = useAuth();
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openAssignment, setOpenAssignment] = useState(null);
  const [viewCert, setViewCert] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch the personal rollup + the full assignment list in parallel.
      // Summary failure must not block the list — we render an empty
      // hero rather than the whole page collapsing.
      const [d, s] = await Promise.all([
        api.get("/my/assignments"),
        api.get("/my/summary").catch(() => null),
      ]);
      setData(d);
      setSummary(s);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pending = useMemo(
    () =>
      (data?.documents || []).filter(
        (a) => a.status !== "completed" && a.status !== "superseded"
      ),
    [data]
  );
  const completed = useMemo(
    () => (data?.documents || []).filter((a) => a.status === "completed"),
    [data]
  );
  // Surface competency assignments that don't have a matching document
  // assignment (e.g. admin assigned a competency whose linked docs weren't
  // available or the competency has no docs yet).
  const pendingCompetencies = useMemo(() => {
    const docCompIds = new Set(
      [...(data?.documents || [])]
        .filter((a) => a.status !== "completed")
        .flatMap((a) => a.document_competency_ids || [])
    );
    return (data?.competencies || []).filter(
      (c) => c.status !== "completed" && !docCompIds.has(c.competency_id)
    );
  }, [data]);

  const nextAction = useMemo(() => {
    // Owner-designed "one thing to do next" pointer. Prefer overdue,
    // then anything in-progress, then the first pending item.
    const list = [...pending, ...pendingCompetencies];
    const overdue = list.find((a) => a.status === "overdue");
    const inProg = list.find((a) => a.status === "in_progress");
    return overdue || inProg || list[0] || null;
  }, [pending, pendingCompetencies]);

  // Learning library = every SOP/policy the staff member has been assigned
  // (open OR completed), rendered as reference cards. Purely a lens over the
  // existing /my/assignments data — no new endpoint, no invented content.
  const library = useMemo(() => {
    return (data?.documents || [])
      .filter((a) => a.status !== "superseded")
      .slice()
      .sort((a, b) => {
        // Show items that need action first, then completed by most recent.
        const aDone = a.status === "completed" ? 1 : 0;
        const bDone = b.status === "completed" ? 1 : 0;
        if (aDone !== bDone) return aDone - bDone;
        return (b.assigned_at || "").localeCompare(a.assigned_at || "");
      });
  }, [data]);

  // Recent activity = real events pulled from actual timestamps. Each entry
  // is derived from data we already have (assigned_at, completed_at,
  // certificate.completed_at). No fabricated updates.
  const activity = useMemo(() => {
    const events = [];
    for (const a of data?.documents || []) {
      if (a.completed_at) {
        events.push({
          id: `d-c-${a.id}`,
          kind: "completed",
          when: a.completed_at,
          title: a.document_title,
          meta: `v${a.document_version || "1.0"}`,
        });
      } else if (a.assigned_at) {
        events.push({
          id: `d-a-${a.id}`,
          kind: "assigned",
          when: a.assigned_at,
          title: a.document_title,
          meta: `v${a.document_version || "1.0"}`,
        });
      }
    }
    for (const c of data?.competencies || []) {
      if (c.completed_at) {
        events.push({
          id: `c-c-${c.id}`,
          kind: "completed",
          when: c.completed_at,
          title: c.competency_name || "Competency",
          meta: "Assessment",
        });
      } else if (c.assigned_at) {
        events.push({
          id: `c-a-${c.id}`,
          kind: "assigned",
          when: c.assigned_at,
          title: c.competency_name || "Competency",
          meta: "Assessment",
        });
      }
    }
    for (const cert of data?.certificates || []) {
      if (cert.completed_at) {
        events.push({
          id: `cert-${cert.cert_id}`,
          kind: "certificate",
          when: cert.completed_at,
          title: cert.module,
          meta: cert.score != null ? `${cert.score}%` : "Certified",
          cert,
        });
      }
    }
    return events
      .filter((e) => e.when)
      .sort((a, b) => (b.when || "").localeCompare(a.when || ""))
      .slice(0, 6);
  }, [data]);

  const rate = summary?.completion_rate ?? 0;

  const hasAnyContent =
    pending.length +
      pendingCompetencies.length +
      completed.length +
      (data?.certificates || []).length >
    0;

  return (
    <AppShell>
      {/* ─── Compliance pulse — greeting + progress ring + inline stats ─── */}
      <section
        data-testid="staff-hub-pulse"
        className="rounded-2xl border border-[#e5dfd0] bg-gradient-to-br from-[#0B1220] via-[#131c31] to-[#1b243c] text-white p-5 md:p-7 mb-5 md:mb-6 relative overflow-hidden"
      >
        <div className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-[#D4A65A]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-emerald-400/10 blur-3xl" />
        <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 md:gap-8 items-center">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-[#D4A65A]">
              My compliance workspace
            </div>
            <h1 className="mt-1.5 font-display text-2xl md:text-[28px] tracking-tight font-semibold leading-tight">
              {greetingFor(user?.name || data?.team_member?.name)}
            </h1>
            <p className="mt-2 text-[13px] md:text-sm text-slate-300 leading-relaxed max-w-lg">
              You're on the {business?.name || "team"} roster. Everything you need to stay
              inspection‑ready lives right here — assessments, evidence and certificates in one place.
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2.5 md:gap-3 md:max-w-md">
              <PulseStat
                icon={Clock}
                label="To do"
                value={pending.length + pendingCompetencies.length}
                tone="amber"
                testid="pulse-todo"
              />
              <PulseStat
                icon={BadgeCheck}
                label="Completed"
                value={completed.length}
                tone="emerald"
                testid="pulse-completed"
              />
              <PulseStat
                icon={Award}
                label="Certificates"
                value={(data?.certificates || []).length}
                tone="gold"
                testid="pulse-certs"
              />
            </div>
          </div>
          <ProgressRing value={rate} testid="pulse-ring" />
        </div>
      </section>

      {/* ─── Quick-nav chips (mobile-first). Anchor jumps only — no new routes. ─── */}
      {!loading && hasAnyContent && (
        <nav
          data-testid="staff-hub-quicknav"
          className="mb-6 -mx-1 flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label="Jump to section"
        >
          {(pending.length + pendingCompetencies.length > 0) && (
            <QuickChip href="#staff-todo" icon={Clock} label="To do" count={pending.length + pendingCompetencies.length} tone="amber" testid="chip-todo" />
          )}
          {activity.length > 0 && (
            <QuickChip href="#staff-activity" icon={History} label="Activity" count={activity.length} testid="chip-activity" />
          )}
          {(data?.certificates || []).length > 0 && (
            <QuickChip href="#staff-certs" icon={Award} label="Certificates" count={(data?.certificates || []).length} tone="gold" testid="chip-certs" />
          )}
          {library.length > 0 && (
            <QuickChip href="#staff-library" icon={Library} label="Library" count={library.length} testid="chip-library" />
          )}
          {completed.length > 0 && (
            <QuickChip href="#staff-records" icon={BadgeCheck} label="Records" count={completed.length} tone="emerald" testid="chip-records" />
          )}
          <QuickChip href="#staff-help" icon={MessageCircle} label="Help" testid="chip-help" />
        </nav>
      )}

      <ErrorBanner error={error} className="mb-5" />
      {loading && <LoadingRow label="Loading your compliance record…" />}

      {/* ─── Next required action — one big tappable card ─── */}
      {!loading && nextAction && (
        <button
          type="button"
          data-testid="staff-hub-next"
          onClick={() =>
            setOpenAssignment(
              nextAction._kind === "competency" || (nextAction.competency_id && !nextAction.document_id)
                ? {
                    id: nextAction.id,
                    _kind: "competency",
                    competency_id: nextAction.competency_id,
                    competency_name: nextAction.competency_name || nextAction.document_title,
                    document_title: nextAction.competency_name || nextAction.document_title,
                    document_version: nextAction.version || "1.0",
                    document_description: `Competency assessment · ${nextAction.category_id || "regulatory"}`,
                    document_competency_ids: [nextAction.competency_id],
                    has_competency: true,
                    status: nextAction.status,
                    assigned_at: nextAction.assigned_at,
                  }
                : nextAction
            )
          }
          className="w-full text-left rounded-2xl border border-[#D4A65A]/50 bg-gradient-to-r from-[#FBF3E0] via-white to-white p-4 md:p-5 mb-6 flex items-center gap-4 hover:shadow-[0_8px_30px_-12px_rgba(11,18,32,0.35)] hover:border-[#D4A65A] transition-all"
        >
          <span className="h-11 w-11 md:h-12 md:w-12 rounded-xl bg-[#0B1220] text-[#D4A65A] flex items-center justify-center shrink-0">
            <Zap size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8C6B33] font-semibold">
              Your next action
            </div>
            <div className="mt-1 font-display text-[15px] md:text-lg tracking-tight text-slate-900 font-semibold line-clamp-2">
              {nextAction.competency_name || nextAction.document_title}
            </div>
            <div className="mt-1 text-[12px] text-slate-500 flex items-center gap-2 flex-wrap">
              <StatusPill status={nextAction.status} />
              {nextAction.assigned_at && (
                <span>Assigned {relativeDate(nextAction.assigned_at)}</span>
              )}
            </div>
          </div>
          <ArrowRight size={20} className="text-[#8C6B33] shrink-0" />
        </button>
      )}

      {!loading && (data?.documents || []).length === 0 && (pendingCompetencies.length === 0) && (
        <EmptyState
          testid="my-training-empty"
          icon={CheckCircle2}
          title="You're all caught up."
          description="Nothing outstanding right now. When your compliance manager assigns you a policy, competency or assessment, it'll appear here — and we'll email you too."
        />
      )}

      {/* ─── Priority list — everything still to do ─── */}
      {(pending.length > 0 || pendingCompetencies.length > 0) && (
        <section id="staff-todo" className="mb-8 scroll-mt-24" data-testid="my-training-pending">
          <SectionHeader
            icon={Clock}
            title="To do"
            count={pending.length + pendingCompetencies.length}
            hint="Complete these to keep your record inspection-ready."
          />
          <div className="space-y-3">
            {pending.map((a) => (
              <AssignmentRow key={a.id} a={a} onOpen={() => setOpenAssignment(a)} />
            ))}
            {pendingCompetencies.map((c) => (
              <CompetencyOnlyRow
                key={c.id}
                c={c}
                onOpen={() =>
                  setOpenAssignment({
                    id: c.id,
                    _kind: "competency",
                    competency_id: c.competency_id,
                    competency_name: c.competency_name,
                    document_title: c.competency_name,
                    document_version: c.version || "1.0",
                    document_description: `Competency assessment · ${c.category_id || "regulatory"}`,
                    document_competency_ids: [c.competency_id],
                    has_competency: true,
                    status: c.status,
                    assigned_at: c.assigned_at,
                  })
                }
              />
            ))}
          </div>
        </section>
      )}

      {/* ─── Recent activity — timeline built from real assigned/completed timestamps ─── */}
      {activity.length > 0 && (
        <section id="staff-activity" className="mb-8 scroll-mt-24" data-testid="my-training-activity">
          <SectionHeader
            icon={History}
            title="Recent activity"
            count={activity.length}
            hint="Everything that's happened on your record lately."
          />
          <ol className="relative border-l border-slate-200 pl-5 space-y-4 ml-1">
            {activity.map((ev) => (
              <ActivityRow
                key={ev.id}
                event={ev}
                onOpenCert={(c) => setViewCert(c)}
              />
            ))}
          </ol>
        </section>
      )}

      {/* ─── Certificates shelf ─── */}
      {(data?.certificates || []).length > 0 && (
        <section id="staff-certs" className="mb-8 scroll-mt-24" data-testid="my-training-certificates">
          <SectionHeader
            icon={Award}
            title="Your certificates"
            count={(data?.certificates || []).length}
            hint="Tap any certificate to view or download the A4 PDF."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(data?.certificates || []).map((c) => (
              <button
                key={c.cert_id}
                onClick={() => setViewCert(c)}
                data-testid={`cert-card-${c.cert_id}`}
                className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-[#D4A65A]/60 hover:shadow-[0_2px_20px_-8px_rgba(11,18,32,0.15)] transition-all group"
              >
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-lg bg-[#D4A65A]/15 text-[#8C6B33] flex items-center justify-center shrink-0 group-hover:bg-[#D4A65A]/25 transition-colors">
                    <Award size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold truncate">
                      {c.cert_id}
                    </div>
                    <div className="mt-1 font-display text-[14.5px] tracking-tight text-slate-900 font-semibold line-clamp-1">
                      {c.module}
                    </div>
                    <div className="mt-1 text-[11.5px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                      <span>v{c.version}</span>
                      <span>·</span>
                      <span>{relativeDate(c.completed_at)}</span>
                      {c.score != null && (<><span>·</span><span className="text-emerald-700 font-semibold">{c.score}%</span></>)}
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-slate-300 group-hover:text-[#8C6B33] shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ─── Learning library — reference shelf of all assigned SOPs (real docs only) ─── */}
      {library.length > 0 && (
        <section id="staff-library" className="mb-8 scroll-mt-24" data-testid="my-training-library">
          <SectionHeader
            icon={Library}
            title="Learning library"
            count={library.length}
            hint="Every policy and SOP assigned to you — open anytime as reference."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {library.map((a) => (
              <LibraryCard
                key={a.id}
                a={a}
                onOpen={() => setOpenAssignment(a)}
              />
            ))}
          </div>
        </section>
      )}

      {/* ─── My records / history — completed items grouped, collapsible on mobile ─── */}
      {completed.length > 0 && (
        <section id="staff-records" className="mb-8 scroll-mt-24" data-testid="my-training-completed">
          <SectionHeader
            icon={BadgeCheck}
            title="My records"
            count={completed.length}
            hint="Your completion history. Tap any row to view the certificate."
          />
          <div className="space-y-3">
            {completed.map((a) => (
              <AssignmentRow
                key={a.id}
                a={a}
                onOpen={() => {
                  const cert = (data?.certificates || []).find((c) => c.cert_id === a.cert_id);
                  if (cert) setViewCert(cert);
                }}
              />
            ))}
          </div>
        </section>
      )}

      {/* ─── Help / contact your manager ─── */}
      <section id="staff-help" className="mb-4 scroll-mt-24" data-testid="staff-hub-help">
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-white to-slate-50 p-5 md:p-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-[#D4A65A]/10 blur-2xl" />
          <div className="relative flex items-start md:items-center gap-4 flex-col md:flex-row">
            <span className="h-11 w-11 rounded-xl bg-[#0B1220] text-[#D4A65A] flex items-center justify-center shrink-0">
              <Lightbulb size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-display text-[16px] md:text-[17px] tracking-tight text-slate-900 font-semibold">
                Need a hand with something?
              </div>
              <p className="mt-1 text-[13px] text-slate-600 leading-relaxed">
                Reach out to your compliance manager at{" "}
                <span className="font-semibold text-slate-900">{business?.name || "your business"}</span>{" "}
                if you're stuck on an assignment, think something was assigned in error, or need any policy
                explained.
              </p>
            </div>
            <a
              href={`mailto:?subject=${encodeURIComponent(
                `${business?.name || "CanniComply"} — I need help with a compliance item`
              )}&body=${encodeURIComponent(
                `Hi,\n\nI'm looking at my CanniComply dashboard and need help with:\n\n[describe here]\n\nThanks,\n${user?.name || ""}`
              )}`}
              data-testid="staff-hub-help-mailto"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0B1220] hover:bg-[#131c31] text-white px-4 py-2.5 text-[12.5px] font-semibold shrink-0 w-full md:w-auto"
            >
              <Mail size={14} /> Contact your manager
              <ArrowRight size={13} />
            </a>
          </div>
        </div>
      </section>

      {openAssignment && (
        <AssignmentDrawer
          assignment={openAssignment}
          onClose={() => setOpenAssignment(null)}
          onCompleted={(cert) => {
            setOpenAssignment(null);
            if (cert) setViewCert(cert);
            load();
          }}
        />
      )}

      {viewCert && (
        <CertificateModal cert={viewCert} onClose={() => setViewCert(null)} />
      )}
    </AppShell>
  );
}

/* ────────── Staff-hub UI helpers (self-contained, no new endpoints) ────────── */

function PulseStat({ icon: Icon, label, value, tone, testid }) {
  const toneCls = {
    amber:   "text-amber-300",
    emerald: "text-emerald-300",
    gold:    "text-[#F0D497]",
  }[tone] || "text-slate-200";
  return (
    <div
      data-testid={testid}
      className="rounded-xl bg-white/[0.06] border border-white/10 backdrop-blur px-3 py-2.5 md:py-3"
    >
      <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold ${toneCls}`}>
        <Icon size={11} strokeWidth={2.4} />
        {label}
      </div>
      <div className="mt-1 font-display text-2xl md:text-[26px] font-semibold tracking-tight tabular-nums">
        {value}
      </div>
    </div>
  );
}

function ProgressRing({ value, testid }) {
  // Fixed 88px ring on mobile, 108px on md+, drawn via SVG so it rasterises
  // cleanly and doesn't rely on conic-gradient (buggy in Safari<15).
  const clamped = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div
      data-testid={testid}
      className="flex items-center justify-center md:justify-end shrink-0"
    >
      <div className="relative h-24 w-24 md:h-28 md:w-28">
        <svg viewBox="0 0 48 48" className="h-full w-full -rotate-90">
          <circle cx="24" cy="24" r="20" stroke="rgba(255,255,255,0.12)" strokeWidth="4" fill="none" />
          <circle
            cx="24" cy="24" r="20"
            stroke="#D4A65A"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
            strokeDasharray={`${(clamped / 100) * (2 * Math.PI * 20)} ${2 * Math.PI * 20}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="font-display text-2xl md:text-[26px] font-semibold tabular-nums leading-none">
            {clamped}<span className="text-sm md:text-base text-[#D4A65A]">%</span>
          </div>
          <div className="mt-0.5 text-[9px] uppercase tracking-[0.18em] text-slate-300 font-semibold">
            On record
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, count, hint }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2">
        <Icon size={15} className="text-[#8C6B33]" />
        <h2 className="font-display text-[15px] md:text-base tracking-tight text-slate-900 font-semibold">
          {title}
        </h2>
        <span className="text-[11px] text-slate-500">· {count}</span>
      </div>
      {hint && <p className="mt-0.5 text-[11.5px] text-slate-500">{hint}</p>}
    </div>
  );
}

function greetingFor(name) {
  const hour = new Date().getHours();
  const time = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return name ? `${time}, ${name.split(" ")[0]}.` : `${time}.`;
}

/* ------------ Quick-nav chip (mobile-first anchor jump) ------------ */

function QuickChip({ href, icon: Icon, label, count, tone, testid }) {
  const toneCls =
    tone === "amber"
      ? "text-amber-700 bg-amber-50 border-amber-200"
      : tone === "emerald"
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : tone === "gold"
      ? "text-[#8C6B33] bg-[#FDF6E7] border-[#D4A65A]/40"
      : "text-slate-700 bg-white border-slate-200";
  return (
    <a
      href={href}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-semibold whitespace-nowrap hover:shadow-[0_2px_10px_-6px_rgba(11,18,32,0.25)] transition-all shrink-0 ${toneCls}`}
    >
      <Icon size={12.5} strokeWidth={2.2} />
      {label}
      {count != null && count > 0 && (
        <span className="tabular-nums text-[10.5px] rounded-full bg-white/70 px-1.5 py-0.5 border border-current/20 ml-0.5">
          {count}
        </span>
      )}
    </a>
  );
}

/* ------------ Activity timeline row (real events only) ------------ */

function ActivityRow({ event, onOpenCert }) {
  const kind = event.kind;
  const config =
    kind === "certificate"
      ? { icon: Award, label: "Certificate issued", pillCls: "bg-[#D4A65A]/15 text-[#8C6B33] border-[#D4A65A]/40" }
      : kind === "completed"
      ? { icon: CheckCircle2, label: "Completed", pillCls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
      : { icon: Clock, label: "Assigned", pillCls: "bg-amber-50 text-amber-800 border-amber-200" };
  const Icon = config.icon;
  const isCert = kind === "certificate" && event.cert;
  const Wrapper = isCert ? "button" : "div";
  return (
    <li className="relative" data-testid={`activity-${event.id}`}>
      <span className="absolute -left-[27px] top-1 h-4 w-4 rounded-full border-2 border-white bg-[#0B1220] flex items-center justify-center shadow-[0_0_0_2px_rgba(212,166,90,0.35)]">
        <Icon size={9} className="text-[#D4A65A]" strokeWidth={3} />
      </span>
      <Wrapper
        onClick={isCert ? () => onOpenCert(event.cert) : undefined}
        className={`w-full text-left rounded-xl border border-slate-200 bg-white p-3.5 flex items-start gap-3 ${
          isCert ? "hover:border-[#D4A65A]/60 hover:shadow-[0_2px_20px_-10px_rgba(11,18,32,0.2)] cursor-pointer transition-all" : ""
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold border rounded px-1.5 py-0.5 ${config.pillCls}`}>
              {config.label}
            </span>
            <span className="text-[11px] text-slate-500">{relativeDate(event.when)}</span>
          </div>
          <div className="mt-1 font-display text-[14px] tracking-tight text-slate-900 font-semibold line-clamp-1">
            {event.title}
          </div>
          {event.meta && (
            <div className="mt-0.5 text-[11.5px] text-slate-500">{event.meta}</div>
          )}
        </div>
        {isCert && <ChevronRight size={15} className="text-slate-300 shrink-0 mt-1" />}
      </Wrapper>
    </li>
  );
}

/* ------------ Learning library card (reference view of an assigned SOP) ------------ */

function LibraryCard({ a, onOpen }) {
  const done = a.status === "completed";
  return (
    <button
      onClick={onOpen}
      data-testid={`library-card-${a.id}`}
      className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-[#D4A65A]/60 hover:shadow-[0_2px_20px_-10px_rgba(11,18,32,0.2)] transition-all group"
    >
      <div className="flex items-start gap-3">
        <span
          className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
            done
              ? "bg-emerald-50 text-emerald-700 group-hover:bg-emerald-100"
              : "bg-[#0B1220] text-[#D4A65A]"
          }`}
        >
          <BookOpen size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <StatusPill status={a.status} />
            <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
              v{a.document_version || "1.0"}
            </span>
          </div>
          <div className="mt-1.5 font-display text-[14px] tracking-tight text-slate-900 font-semibold line-clamp-1">
            {a.document_title}
          </div>
          {a.document_description && (
            <div className="mt-0.5 text-[11.5px] text-slate-500 line-clamp-2 leading-snug">
              {a.document_description}
            </div>
          )}
          <div className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#8C6B33] group-hover:text-[#0B1220] transition-colors">
            {done ? "Re-read" : "Open"}
            <ArrowRight size={11} />
          </div>
        </div>
      </div>
    </button>
  );
}

function Stat({ label, value, tone }) {
  const toneCls =
    tone === "emerald"
      ? "text-emerald-700"
      : tone === "amber"
      ? "text-amber-700"
      : tone === "gold"
      ? "text-[#8C6B33]"
      : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
        {label}
      </div>
      <div className={`mt-1 font-display text-2xl font-semibold ${toneCls}`}>{value}</div>
    </div>
  );
}

/* ------------ Summary hero (wires /api/my/summary) ------------ */

function SummaryHero({ summary }) {
  const rate = Math.max(0, Math.min(100, summary.completion_rate || 0));
  const counts = summary.counts || {};
  const overdue = counts.overdue || 0;
  const openTotal = (counts.documents_open || 0) + (counts.competencies_open || 0);

  // Radial progress geometry.
  const size = 96;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (rate / 100) * c;

  const ringColor = rate >= 80 ? "#0F766E" : rate >= 40 ? "#8C6B33" : "#B45309";

  return (
    <div
      data-testid="my-training-summary-hero"
      className="mb-6 rounded-2xl border border-slate-200 bg-white overflow-hidden"
    >
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-center gap-6 p-5 md:p-6">
        {/* Radial progress */}
        <div className="flex items-center gap-4 min-w-0">
          <div className="relative shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
              <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke="#E2E8F0"
                strokeWidth={stroke}
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={ringColor}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={`${dash} ${c}`}
                style={{ transition: "stroke-dasharray 500ms ease" }}
              />
            </svg>
            <div
              className="absolute inset-0 flex flex-col items-center justify-center"
              data-testid="my-training-summary-rate"
            >
              <span
                className="font-display font-bold text-slate-900 leading-none"
                style={{ fontSize: 22 }}
              >
                {rate}%
              </span>
              <span className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-semibold mt-1">
                Compliant
              </span>
            </div>
          </div>

          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
              Personal compliance
            </div>
            <div className="mt-1 font-display text-[19px] tracking-tight text-slate-900 font-semibold truncate">
              {rate >= 100
                ? "All caught up — nothing pending."
                : openTotal === 0
                ? "Nothing outstanding right now."
                : `${openTotal} item${openTotal === 1 ? "" : "s"} still to complete.`}
            </div>
            <div className="mt-1 text-[13px] text-slate-500 truncate">
              {summary.team_member?.job_title || "Team member"}
              {summary.team_member?.department ? ` · ${summary.team_member.department}` : ""}
            </div>
          </div>
        </div>

        {/* Mini stats */}
        <div className="grid grid-cols-3 gap-2" data-testid="my-training-summary-counts">
          <MiniStat
            label="Documents"
            done={counts.documents_completed || 0}
            total={counts.documents_total || 0}
            testid="summary-docs"
          />
          <MiniStat
            label="Competencies"
            done={counts.competencies_completed || 0}
            total={counts.competencies_total || 0}
            testid="summary-comps"
          />
          <MiniStat
            label="Certificates"
            done={counts.certificates_total || 0}
            total={counts.certificates_total || 0}
            tone="gold"
            hideRatio
            testid="summary-certs"
          />
        </div>

        {/* Overdue callout */}
        <div className="justify-self-start md:justify-self-end">
          {overdue > 0 ? (
            <div
              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700"
              data-testid="my-training-summary-overdue"
            >
              <AlertTriangle size={16} className="shrink-0" />
              <div className="text-[13px] font-semibold">
                {overdue} overdue &middot; needs action today
              </div>
            </div>
          ) : (
            <div
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800"
              data-testid="my-training-summary-onhand"
            >
              <ShieldCheck size={16} className="shrink-0" />
              <div className="text-[13px] font-semibold">Nothing overdue</div>
            </div>
          )}
        </div>
      </div>

      {/* Next up strip */}
      {(summary.next_up || []).length > 0 && (
        <div
          className="border-t border-slate-100 bg-slate-50/60 px-5 md:px-6 py-3.5"
          data-testid="my-training-summary-nextup"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-slate-500 font-semibold shrink-0">
              Next up
            </div>
            <ul className="flex items-center gap-3 flex-wrap min-w-0">
              {(summary.next_up || []).slice(0, 3).map((it) => (
                <li
                  key={it.assignment_id}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[12.5px] text-slate-700"
                  data-testid={`summary-nextup-${it.assignment_id}`}
                >
                  {it.kind === "competency" ? (
                    <BadgeCheck size={13} className="text-[#8C6B33] shrink-0" />
                  ) : (
                    <FileText size={13} className="text-slate-500 shrink-0" />
                  )}
                  <span className="truncate max-w-[220px] font-medium">
                    {it.title}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, done, total, tone, hideRatio, testid }) {
  const ratio = total > 0 ? Math.round((done / total) * 100) : 0;
  const barColor =
    tone === "gold"
      ? "#8C6B33"
      : ratio >= 100
      ? "#0F766E"
      : ratio >= 40
      ? "#8C6B33"
      : "#B45309";
  return (
    <div className="rounded-lg bg-slate-50/70 px-3 py-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="font-display text-[20px] font-semibold text-slate-900 leading-none">
          {done}
        </span>
        {!hideRatio && total > 0 && (
          <span className="text-[11.5px] text-slate-500">/ {total}</span>
        )}
      </div>
      {!hideRatio && total > 0 && (
        <div className="mt-2 h-1 w-full rounded-full bg-slate-200 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${ratio}%`, backgroundColor: barColor }}
          />
        </div>
      )}
    </div>
  );
}

function AssignmentRow({ a, onOpen }) {
  const hasComp = a.has_competency;
  return (
    <div
      data-testid={`assignment-row-${a.id}`}
      className="rounded-xl border border-slate-200 bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
    >
      <span
        className={`h-11 w-11 rounded-lg flex items-center justify-center shrink-0 ${
          a.status === "completed"
            ? "bg-emerald-50 text-emerald-700"
            : "bg-[#0B1220] text-[#D4A65A]"
        }`}
      >
        {a.status === "completed" ? <CheckCircle2 size={18} /> : <FileText size={18} />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-slate-900 text-[15px] font-semibold tracking-tight truncate">
            {a.document_title}
          </div>
          <span className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 bg-slate-100 rounded px-2 py-0.5">
            v{a.document_version}
          </span>
          <StatusPill status={a.status} />
          {hasComp && (
            <span
              className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#8C6B33] bg-[#FDF6E7] border border-[#D4A65A]/40 rounded px-2 py-0.5"
              title="Requires assessment"
            >
              <Sparkles size={10} /> Assessment
            </span>
          )}
        </div>
        {a.document_description && (
          <p className="mt-1 text-[12.5px] text-slate-500 line-clamp-2">{a.document_description}</p>
        )}
        <div className="mt-2 flex items-center flex-wrap gap-x-3 text-[11.5px] text-slate-500">
          <span>Assigned {relativeDate(a.assigned_at)}</span>
          {a.completed_at && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-emerald-700">Completed {relativeDate(a.completed_at)}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onOpen}
          data-testid={`assignment-open-${a.id}`}
          className={`text-[12px] font-semibold rounded-lg px-3 py-2 inline-flex items-center gap-1.5 ${
            a.status === "completed"
              ? "border border-slate-200 text-slate-700 hover:border-slate-300"
              : "bg-[#D4A65A] hover:bg-[#E6BE7A] text-[#0B1220]"
          }`}
        >
          {a.status === "completed"
            ? "View certificate"
            : hasComp
            ? "Start"
            : "Read & sign"}
          <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

/* Competency assigned but no doc assignment for it — assessment-only card. */
function CompetencyOnlyRow({ c, onOpen }) {
  return (
    <div
      data-testid={`competency-row-${c.id}`}
      className="rounded-xl border border-slate-200 bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
    >
      <span className="h-11 w-11 rounded-lg bg-[#0B1220] text-[#D4A65A] flex items-center justify-center shrink-0">
        <Sparkles size={18} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-slate-900 text-[15px] font-semibold tracking-tight truncate">
            {c.competency_name || c.competency_id}
          </div>
          <StatusPill status={c.status} />
          <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#8C6B33] bg-[#FDF6E7] border border-[#D4A65A]/40 rounded px-2 py-0.5">
            <Sparkles size={10} /> Assessment only
          </span>
        </div>
        <p className="mt-1 text-[12.5px] text-slate-500 line-clamp-2">
          Competency assessment — no linked SOP to read. Complete the assessment to earn your certificate.
        </p>
        <div className="mt-2 text-[11.5px] text-slate-500">
          Assigned {relativeDate(c.assigned_at)}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onOpen}
          data-testid={`competency-open-${c.id}`}
          className="text-[12px] font-semibold rounded-lg px-3 py-2 inline-flex items-center gap-1.5 bg-[#D4A65A] hover:bg-[#E6BE7A] text-[#0B1220]"
        >
          Start assessment <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

/* ------------ Assignment drawer ------------ */

function AssignmentDrawer({ assignment, onClose, onCompleted }) {
  const { user } = useAuth();
  const isCompetencyOnly = assignment._kind === "competency";
  const hasComp = assignment.has_competency;
  const initialStage = isCompetencyOnly
    ? "assessment-loading"
    : hasComp
    ? "learn-loading"
    : "read";
  const [stage, setStage] = useState(initialStage);
  const [confirmed, setConfirmed] = useState(false);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(!isCompetencyOnly);
  const [pdfError, setPdfError] = useState("");
  const [showSopModal, setShowSopModal] = useState(false);

  // Global Escape closes the drawer (unless a nested SOP modal is open,
  // in which case Escape closes that first). Keyboard-accessible UX per
  // the launch-readiness audit (iteration_19).
  useEffect(() => {
    function onKey(e) {
      if (e.key !== "Escape") return;
      if (showSopModal) {
        setShowSopModal(false);
      } else {
        onClose?.();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, showSopModal]);

  // Assessment state
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [certificate, setCertificate] = useState(null);

  useEffect(() => {
    if (isCompetencyOnly) {
      // Auto-load the assessment when the assignment has no document to read.
      (async () => {
        try {
          const data = await api.get(`/my/assessment/${assignment.id}`);
          setAssessment(data);
          setStage("assessment");
        } catch (e) {
          setError(e.message);
          setStage("assessment-error");
        }
      })();
      return;
    }
    // For hasComp assignments we auto-load the AI learning module first so
    // staff open into the training course, not the raw PDF viewer. The SOP
    // PDF still loads in parallel — it's available via the "View original
    // SOP" link inside the learning stage and any staff can drop back to it.
    let revoked = null;
    if (hasComp) {
      (async () => {
        try {
          const data = await api.get(`/my/assessment/${assignment.id}`);
          // Compliance-critical defensive guard: refuse to display the
          // training course if the module the backend returned belongs to
          // a different document. This prevents any lingering cross-doc
          // fallback from ever showing the wrong SOP's content.
          const modDocId = data?.learning_module?.document_id;
          if (modDocId && modDocId !== assignment.document_id) {
            setStage("read");
            return;
          }
          setAssessment(data);
          setStage("learn");
        } catch (e) {
          // 409 = no approved module for this specific document.
          // Fall back to the plain acknowledgement flow (read → sign).
          const msg = (e && e.message) || "";
          if (msg.includes("409") || /no approved (assessment|module)/i.test(msg)) {
            setStage("read");
            return;
          }
          setError(msg);
          setStage("assessment-error");
        }
      })();
    }
    (async () => {
      setPdfLoading(true);
      setPdfError("");
      try {
        const res = await fetch(
          `${API}/my/documents/${assignment.id}/download`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        if (!res.ok) {
          const t = await res.text();
          throw new Error(t || `PDF unavailable (${res.status})`);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoked = url;
        setPdfBlobUrl(url);
      } catch (e) {
        setPdfError(e.message);
      } finally {
        setPdfLoading(false);
      }
    })();
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [assignment.id, isCompetencyOnly, hasComp]);

  async function goNext() {
    setError("");
    if (!hasComp) {
      setStage("ack");
      return;
    }
    // hasComp flow: from the learning stage we move straight into the
    // assessment (module + questions have already been fetched on open).
    if (assessment) {
      setStage("assessment");
      return;
    }
    setBusy(true);
    try {
      const data = await api.get(`/my/assessment/${assignment.id}`);
      setAssessment(data);
      setStage("assessment");
    } catch (e) {
      setError(e.message);
      setStage("assessment-error");
    } finally {
      setBusy(false);
    }
  }

  async function submitAck() {
    if (!confirmed) {
      setError("You must confirm you have read and understood.");
      return;
    }
    if (signature.trim().length < 2) {
      setError("Type your full name to sign.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api.post(
        `/my/documents/${assignment.id}/acknowledge`,
        { signature: signature.trim(), confirm_understood: true }
      );
      onCompleted(res.certificate || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function submitAssessment() {
    if (!assessment) return;
    const missing = (assessment.questions || []).filter((q) => !answers[q.id]);
    if (missing.length > 0) {
      setError(`Please answer all ${assessment.questions.length} questions (${missing.length} still empty).`);
      return;
    }
    if (signature.trim().length < 2) {
      setError("Type your full name to sign.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await api.post(`/my/assessment/${assignment.id}/submit`, {
        answers,
        signature: signature.trim(),
      });
      // Pass AND fail both route through the "result" stage now — passing
      // renders the professional completion summary; failing renders the
      // per-question review. On pass we hold the certificate in local state
      // so the summary's "View certificate" button can hand it to the
      // parent (which opens the existing CertificateModal).
      setResult(res);
      setCertificate(res.certificate || null);
      setStage("result");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-stretch md:items-center justify-center p-0 md:p-4">
      <div
        className="w-full md:max-w-3xl md:h-[90vh] bg-white md:rounded-2xl shadow-xl border border-slate-200 flex flex-col overflow-hidden"
        data-testid="assignment-drawer"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8C6B33] font-semibold">
              {hasComp ? "Policy + competency assessment" : "Policy acknowledgement"}
            </div>
            <div className="mt-1 font-display text-lg tracking-tight text-slate-900 font-semibold truncate">
              {assignment.document_title}
            </div>
            <div className="mt-1 text-[12px] text-slate-500">
              v{assignment.document_version}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="assignment-drawer-close"
            className="text-slate-400 hover:text-slate-700 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Stage indicator */}
        {!isCompetencyOnly && <StageBar hasComp={hasComp} stage={stage} />}
        {isCompetencyOnly && (
          <div className="px-5 py-2.5 border-b border-slate-100 flex items-center gap-2 text-[11px] text-slate-600">
            <span className="inline-flex items-center gap-1.5 font-semibold rounded-full px-2.5 py-1 bg-[#0B1220] text-[#D4A65A]">
              <span className="text-[10px]">1</span> Assessment
            </span>
            <span className="inline-flex items-center gap-1.5 font-semibold rounded-full px-2.5 py-1 bg-slate-100 text-slate-500">
              <span className="text-[10px]">2</span> Certificate
            </span>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5">
          {stage === "assessment-loading" && (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm gap-2 py-16">
              <Loader2 size={16} className="animate-spin" /> Loading your assessment…
            </div>
          )}

          {stage === "assessment-error" && (
            <div data-testid="assignment-stage-assessment-error" className="py-8">
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
                <div className="flex items-center gap-2 text-amber-800 font-semibold">
                  <AlertTriangle size={16} /> Assessment not ready
                </div>
                <p className="mt-2 text-[13.5px] text-amber-900 leading-relaxed">
                  {error || "This assessment isn't available yet."}
                </p>
                <p className="mt-3 text-[12.5px] text-amber-800">
                  You can close this window and come back once your compliance
                  manager has approved the AI-drafted learning module.
                </p>
              </div>
            </div>
          )}

          {stage === "learn-loading" && (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm gap-2 py-16">
              <Loader2 size={16} className="animate-spin" /> Preparing your training course…
            </div>
          )}

          {stage === "learn" && assessment && (
            <LearningModuleView
              module={assessment.learning_module}
              questionCount={assessment.questions?.length || 0}
              passMark={assessment.pass_mark}
              sopAvailable={!!pdfBlobUrl && !pdfError}
              onOpenSop={() => setShowSopModal(true)}
            />
          )}

          {stage === "read" && (
            <div data-testid="assignment-stage-read">
              <div className="text-[13px] text-slate-600 leading-relaxed mb-4">
                Read the document below carefully.{" "}
                {hasComp
                  ? "You'll then complete a short assessment before your certificate is issued."
                  : "You'll confirm your understanding and sign to acknowledge."}
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden" style={{ height: "60vh" }}>
                {pdfLoading && (
                  <div className="h-full flex items-center justify-center text-slate-500 text-sm gap-2">
                    <Loader2 size={16} className="animate-spin" /> Loading document…
                  </div>
                )}
                {pdfError && (
                  <div className="h-full flex items-center justify-center text-rose-700 text-sm gap-2 px-4 text-center">
                    <AlertTriangle size={16} /> Could not load document: {pdfError}
                  </div>
                )}
                {pdfBlobUrl && !pdfLoading && !pdfError && (
                  <iframe
                    src={pdfBlobUrl}
                    title={assignment.document_title}
                    className="w-full h-full"
                    data-testid="assignment-pdf-frame"
                  />
                )}
              </div>
            </div>
          )}

          {stage === "ack" && (
            <div data-testid="assignment-stage-ack">
              <h3 className="font-display text-lg tracking-tight text-slate-900 font-semibold">
                Confirm and sign
              </h3>
              <p className="mt-2 text-slate-500 text-[13.5px] leading-relaxed">
                By signing below you confirm you have read, understood and will
                comply with <b>{assignment.document_title}</b> (v{assignment.document_version}).
              </p>
              <label className="mt-5 flex items-start gap-2.5 rounded-lg border border-slate-200 p-3 cursor-pointer hover:border-slate-300">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  data-testid="ack-confirm-checkbox"
                  className="mt-1"
                />
                <span className="text-[13px] text-slate-700 leading-relaxed">
                  I confirm I have read and understood this policy and I will
                  comply with it. I understand this acknowledgement will be
                  recorded against my compliance record.
                </span>
              </label>
              <label className="mt-4 block">
                <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                  Type your full name to sign
                </span>
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  data-testid="ack-signature"
                  placeholder="Your full name"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
                />
              </label>
            </div>
          )}

          {stage === "assessment" && assessment && (
            <div data-testid="assignment-stage-assessment">
              <h3 className="font-display text-lg tracking-tight text-slate-900 font-semibold">
                Assessment — {assessment.learning_module?.competency_name || "Competency"}
              </h3>
              <p className="mt-2 text-slate-500 text-[13px]">
                Answer all {assessment.questions.length} questions. Pass mark {assessment.pass_mark}%.
              </p>
              <div className="mt-5 space-y-4">
                {assessment.questions.map((q, i) => (
                  <div key={q.id} className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                        Question {i + 1}
                      </span>
                      <span className={`text-[10px] uppercase tracking-[0.14em] font-semibold border rounded px-1.5 py-0.5 ${
                        q.type === "scenario"
                          ? "border-[#D4A65A]/40 bg-[#FDF6E7] text-[#8C6B33]"
                          : "border-slate-200 bg-slate-100 text-slate-600"
                      }`}>
                        {q.type === "scenario" ? "Scenario" : "MCQ"}
                      </span>
                    </div>
                    <div className="mt-2 text-[14px] text-slate-900 font-medium leading-relaxed">
                      {q.prompt}
                    </div>
                    <div className="mt-3 space-y-1.5">
                      {(q.options || []).map((opt) => {
                        const picked = answers[q.id] === opt.key;
                        return (
                          <label
                            key={opt.key}
                            data-testid={`q-${q.id}-option-${opt.key}`}
                            className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                              picked
                                ? "border-[#D4A65A] bg-[#FDF6E7]"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`q-${q.id}`}
                              value={opt.key}
                              checked={picked}
                              onChange={() =>
                                setAnswers((a) => ({ ...a, [q.id]: opt.key }))
                              }
                              className="mt-1"
                            />
                            <span className="text-[13px] text-slate-800">
                              <b className="mr-1.5">{opt.key}.</b> {opt.text}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <label className="mt-6 block">
                <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
                  Sign your submission
                </span>
                <input
                  type="text"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  data-testid="assessment-signature"
                  placeholder="Your full name"
                  className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:border-[#D4A65A] focus:ring-2 focus:ring-[#D4A65A]/20 focus:outline-none transition-colors"
                />
              </label>
            </div>
          )}

          {stage === "result" && result && (
            <CourseCompletionSummary
              result={result}
              assessment={assessment}
              assignment={assignment}
              certificate={certificate}
              user={user}
            />
          )}
        </div>

        {error && (
          <div className="px-5 pb-2">
            <div data-testid="assignment-error" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">
              {error}
            </div>
          </div>
        )}

        {/* Footer buttons */}
        <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between gap-2 bg-slate-50/40">
          {(stage === "assessment-loading" || stage === "learn-loading") && (
            <div className="text-[11.5px] text-slate-500">
              {stage === "learn-loading" ? "Preparing training course…" : "Preparing assessment…"}
            </div>
          )}
          {stage === "assessment-error" && (
            <>
              <div className="text-[11.5px] text-slate-500">Assessment not yet approved</div>
              <button
                onClick={onClose}
                data-testid="assignment-error-close"
                className="text-[12px] font-semibold bg-[#0B1220] hover:bg-[#131c31] text-white rounded-lg px-3.5 py-2"
              >
                Close
              </button>
            </>
          )}
          {stage === "learn" && (
            <>
              <div className="text-[11.5px] text-slate-500">
                Step 1 of 3 · Training course
              </div>
              <button
                onClick={goNext}
                disabled={busy}
                data-testid="assignment-continue-btn"
                className="text-[12px] font-semibold bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white rounded-lg px-3.5 py-2 inline-flex items-center gap-1.5"
              >
                {busy ? "Loading…" : "Continue to knowledge check"}
                <ArrowRight size={12} />
              </button>
            </>
          )}
          {stage === "read" && (
            <>
              <div className="text-[11.5px] text-slate-500">Step 1 of {hasComp ? "3" : "2"}</div>
              <button
                onClick={goNext}
                disabled={busy || pdfLoading || !!pdfError}
                data-testid="assignment-continue-btn"
                className="text-[12px] font-semibold bg-[#0B1220] hover:bg-[#131c31] disabled:opacity-40 text-white rounded-lg px-3.5 py-2 inline-flex items-center gap-1.5"
              >
                {busy ? "Loading…" : hasComp ? "Continue to assessment" : "Continue to sign"}
                <ArrowRight size={12} />
              </button>
            </>
          )}
          {stage === "ack" && (
            <>
              <button
                onClick={() => setStage("read")}
                className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 rounded-lg px-3 py-2"
              >
                Back
              </button>
              <button
                onClick={submitAck}
                disabled={busy || !confirmed || signature.trim().length < 2}
                data-testid="assignment-ack-submit"
                className="text-[12px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 rounded-lg px-3.5 py-2 inline-flex items-center gap-1.5"
              >
                <ShieldCheck size={13} /> {busy ? "Submitting…" : "Sign & complete"}
              </button>
            </>
          )}
          {stage === "assessment" && (
            <>
              <button
                onClick={() => setStage(hasComp ? "learn" : "read")}
                className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 rounded-lg px-3 py-2"
              >
                Back
              </button>
              <button
                onClick={submitAssessment}
                disabled={busy}
                data-testid="assignment-assessment-submit"
                className="text-[12px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 rounded-lg px-3.5 py-2 inline-flex items-center gap-1.5"
              >
                <ShieldCheck size={13} /> {busy ? "Marking…" : "Submit assessment"}
              </button>
            </>
          )}
          {stage === "result" && result?.passed && (
            <>
              <button
                onClick={onClose}
                data-testid="assignment-passed-close"
                className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 rounded-lg px-3 py-2"
              >
                Close
              </button>
              <button
                onClick={() => onCompleted(certificate)}
                data-testid="assignment-view-cert-btn"
                className="text-[12px] font-semibold bg-[#D4A65A] hover:bg-[#E6BE7A] text-[#0B1220] rounded-lg px-3.5 py-2 inline-flex items-center gap-1.5"
              >
                <Award size={13} /> View certificate
                <ArrowRight size={12} />
              </button>
            </>
          )}
          {stage === "result" && result && !result.passed && (
            <>
              <button
                onClick={() => {
                  setResult(null);
                  setStage("assessment");
                  setAnswers({});
                }}
                data-testid="assignment-result-retry"
                className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 rounded-lg px-3 py-2 inline-flex items-center gap-1.5"
              >
                <RefreshCw size={12} /> Retry assessment
              </button>
              <button
                onClick={onClose}
                data-testid="assignment-result-close"
                className="text-[12px] font-semibold bg-[#0B1220] hover:bg-[#131c31] text-white rounded-lg px-3.5 py-2"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>

      {showSopModal && pdfBlobUrl && (
        <SopViewerModal
          url={pdfBlobUrl}
          title={assignment.document_title}
          version={assignment.document_version}
          onClose={() => setShowSopModal(false)}
        />
      )}
    </div>
  );
}

function StageBar({ hasComp, stage }) {
  const steps = hasComp
    ? [
        { key: "learn", label: "Training course" },
        { key: "assessment", label: "Knowledge check" },
        { key: "done", label: "Certificate" },
      ]
    : [
        { key: "read", label: "Read document" },
        { key: "ack", label: "Sign & complete" },
      ];
  const activeIdx = steps.findIndex(
    (s) =>
      s.key === stage ||
      (s.key === "learn" && stage === "learn-loading") ||
      (s.key === "assessment" && stage === "result")
  );
  return (
    <div className="px-5 py-2.5 border-b border-slate-100 flex items-center gap-2 text-[11px] text-slate-600 flex-wrap">
      {steps.map((s, i) => (
        <span
          key={s.key}
          className={`inline-flex items-center gap-1.5 font-semibold rounded-full px-2.5 py-1 ${
            i <= activeIdx
              ? "bg-[#0B1220] text-[#D4A65A]"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          <span className="text-[10px]">{i + 1}</span> {s.label}
        </span>
      ))}
    </div>
  );
}

/* ------------ Learning module (training course) view ------------ */

function LearningModuleView({ module, questionCount, passMark, sopAvailable, onOpenSop }) {
  if (!module) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500 text-sm gap-2 py-16">
        <Loader2 size={16} className="animate-spin" /> Preparing course content…
      </div>
    );
  }
  const sections = Array.isArray(module.sections) ? module.sections : [];
  return (
    <div data-testid="assignment-stage-learn" className="max-w-2xl mx-auto">
      {/* Course header */}
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-[#0B1220] to-[#131c31] text-white p-5 md:p-6 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#D4A65A]/10 blur-2xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] font-semibold text-[#D4A65A]">
            <GraduationCap size={14} /> Training course
          </div>
          <h2 className="mt-3 font-display text-2xl md:text-[26px] tracking-tight font-semibold leading-tight">
            {module.title || module.competency_name || "Compliance training"}
          </h2>
          {module.summary && (
            <p className="mt-3 text-[13.5px] text-white/80 leading-relaxed max-w-xl">
              {module.summary}
            </p>
          )}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-white/10 border border-white/15 rounded-full px-2.5 py-1">
              <BookOpen size={11} /> {sections.length} section{sections.length === 1 ? "" : "s"}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-white/10 border border-white/15 rounded-full px-2.5 py-1">
              <Target size={11} /> {questionCount} question knowledge check
            </span>
            {passMark != null && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-white/10 border border-white/15 rounded-full px-2.5 py-1">
                <BadgeCheck size={11} /> Pass mark {passMark}%
              </span>
            )}
          </div>
          {sopAvailable && (
            <button
              onClick={onOpenSop}
              data-testid="learn-view-sop-btn"
              className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#D4A65A] hover:text-[#E6BE7A] underline underline-offset-4 decoration-[#D4A65A]/40"
            >
              <FileText size={12} /> View original SOP
            </button>
          )}
        </div>
      </div>

      {/* Sections */}
      <ol
        className="mt-6 space-y-5"
        data-testid="learn-sections"
      >
        {sections.map((s, i) => (
          <li
            key={i}
            className="rounded-xl border border-slate-200 bg-white overflow-hidden"
            data-testid={`learn-section-${i}`}
          >
            <div className="px-5 pt-5 pb-4 border-b border-slate-100 bg-gradient-to-b from-slate-50/60 to-white">
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#8C6B33] font-semibold">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#0B1220] text-[#D4A65A] text-[10px] font-bold">
                  {i + 1}
                </span>
                Section {i + 1} of {sections.length}
              </div>
              <h3 className="mt-2 font-display text-[18px] tracking-tight text-slate-900 font-semibold leading-snug">
                {s.heading || "Untitled section"}
              </h3>
            </div>
            <div className="px-5 py-5 space-y-3">
              {(s.body || "")
                .split(/\n{2,}/)
                .filter((p) => p.trim())
                .map((p, k) => (
                  <p key={k} className="text-[14px] text-slate-700 leading-[1.7]">
                    {p.trim()}
                  </p>
                ))}
              {Array.isArray(s.key_takeaways) && s.key_takeaways.length > 0 && (
                <div className="mt-4 rounded-lg border border-[#D4A65A]/40 bg-[#FDF6E7] p-4">
                  <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-[#8C6B33] font-semibold">
                    <Lightbulb size={12} /> Key takeaways
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {s.key_takeaways.map((t, k) => (
                      <li
                        key={k}
                        className="flex items-start gap-2 text-[13px] text-slate-800 leading-relaxed"
                      >
                        <CheckCircle2
                          size={13}
                          className="text-[#8C6B33] mt-1 shrink-0"
                        />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {s.source_section && (
                <div className="mt-3 flex items-start gap-2 text-[11.5px] text-slate-500 border-t border-slate-100 pt-3">
                  <Quote size={11} className="mt-0.5 shrink-0 text-slate-400" />
                  <span>
                    <span className="uppercase tracking-[0.14em] font-semibold text-slate-500 mr-1.5">
                      From the source document:
                    </span>
                    {s.source_section}
                  </span>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>

      {/* Ready-to-test gate */}
      <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
          <ShieldCheck size={16} />
        </span>
        <div>
          <div className="text-[13.5px] font-semibold text-slate-900">
            Ready when you are.
          </div>
          <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
            Take your time — you can scroll back through this material at any
            point. When you're confident, continue to the knowledge check to
            earn your certificate.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ------------ Course completion summary (result stage) ------------ */

function CourseCompletionSummary({ result, assessment, assignment, certificate, user }) {
  const passed = !!result.passed;
  const perQuestion = Array.isArray(result.per_question) ? result.per_question : [];
  const correctItems = perQuestion.filter((q) => q.is_correct);
  const incorrectItems = perQuestion.filter((q) => !q.is_correct);
  const questions = Array.isArray(assessment?.questions) ? assessment.questions : [];
  const questionById = Object.fromEntries(questions.map((q) => [q.id, q]));
  const module = assessment?.learning_module;
  const sections = Array.isArray(module?.sections) ? module.sections : [];
  const aggregatedTakeaways = sections
    .flatMap((s) => (Array.isArray(s.key_takeaways) ? s.key_takeaways : []))
    .filter(Boolean)
    .slice(0, 6);
  const competencyName =
    module?.competency_name ||
    certificate?.module ||
    assignment.competency_name ||
    assignment.document_title ||
    "Competency";
  const completedAtIso = certificate?.completed_at || new Date().toISOString();
  const completedDate = new Date(completedAtIso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const completedTime = new Date(completedAtIso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <div
      data-testid={passed ? "assignment-stage-passed" : "assignment-stage-result"}
      className="max-w-2xl mx-auto space-y-5"
    >
      {/* HERO */}
      <div
        className={`rounded-2xl border p-6 md:p-7 relative overflow-hidden ${
          passed
            ? "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white"
            : "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white"
        }`}
      >
        <div
          className={`absolute -right-10 -top-10 h-40 w-40 rounded-full blur-3xl ${
            passed ? "bg-emerald-300/30" : "bg-amber-300/30"
          }`}
        />
        <div className="relative">
          <div
            className={`inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] font-semibold ${
              passed ? "text-emerald-800" : "text-amber-800"
            }`}
          >
            {passed ? <Trophy size={14} /> : <RefreshCw size={14} />}
            {passed ? "Course completed" : "Almost there"}
          </div>
          <h2 className="mt-2 font-display text-[26px] md:text-[30px] tracking-tight text-slate-900 font-semibold leading-tight">
            {passed
              ? `Well done, ${firstName} — you passed.`
              : `Close — but not quite yet, ${firstName}.`}
          </h2>
          <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed max-w-xl">
            {passed
              ? "Your competency record has been signed, timestamped and added to your compliance file. Below is your training summary and certificate."
              : `You need ${result.pass_mark}% to earn your certificate. Review the material and incorrect answers below, then retry when you're ready.`}
          </p>

          {/* Score + pass/fail */}
          <div className="mt-6 flex flex-wrap items-end gap-6">
            <div>
              <div
                className={`font-display text-6xl md:text-7xl tracking-tight font-semibold leading-none ${
                  passed ? "text-emerald-700" : "text-amber-700"
                }`}
                data-testid={passed ? "assignment-passed-score" : "assignment-failed-score"}
              >
                {result.score_pct}%
              </div>
              <div className="mt-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                Overall score
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span
                className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold border rounded-full px-3 py-1 ${
                  passed
                    ? "border-emerald-300 bg-emerald-100 text-emerald-800"
                    : "border-amber-300 bg-amber-100 text-amber-800"
                }`}
              >
                {passed ? <BadgeCheck size={12} /> : <AlertTriangle size={12} />}
                {passed ? "Passed" : "Not passed"}
              </span>
              <span className="text-[11.5px] text-slate-500">
                Pass mark {result.pass_mark}% · {result.correct}/{result.total} correct
              </span>
            </div>
          </div>

          {/* Score bar */}
          <div className="mt-5 h-2 w-full rounded-full bg-slate-200 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                passed ? "bg-emerald-600" : "bg-amber-500"
              }`}
              style={{ width: `${Math.max(4, result.score_pct)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <dl
        className="grid sm:grid-cols-2 gap-3"
        data-testid={passed ? "assignment-passed-summary" : "assignment-failed-summary"}
      >
        <SummaryCard
          icon={<Target size={14} className="text-[#8C6B33]" />}
          label="Competency achieved"
          testid={passed ? "assignment-passed-competency" : "assignment-failed-competency"}
        >
          {competencyName}
        </SummaryCard>
        <SummaryCard
          icon={<FileText size={14} className="text-slate-500" />}
          label="Module"
          testid={passed ? "assignment-passed-module" : "assignment-failed-module"}
        >
          {assignment.document_title || competencyName}
          {assignment.document_version && (
            <span className="text-slate-500 font-normal"> · v{assignment.document_version}</span>
          )}
        </SummaryCard>
        <SummaryCard
          icon={<Clock size={14} className="text-slate-500" />}
          label="Completed"
          testid={passed ? "assignment-passed-date" : "assignment-failed-date"}
        >
          {completedDate}
          <span className="text-slate-500 font-normal"> · {completedTime}</span>
        </SummaryCard>
        <SummaryCard
          icon={<Award size={14} className="text-[#8C6B33]" />}
          label="Certificate"
          testid={passed ? "assignment-passed-cert-id" : "assignment-failed-cert-id"}
        >
          {passed && certificate?.cert_id ? (
            <span className="font-mono">{certificate.cert_id}</span>
          ) : (
            <span className="text-slate-500 font-normal">
              {passed ? "Ready to view" : "Not issued yet"}
            </span>
          )}
        </SummaryCard>
      </dl>

      {/* Learning summary */}
      {module?.summary && (
        <section
          className="rounded-xl border border-slate-200 bg-white p-5"
          data-testid="completion-learning-summary"
        >
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#8C6B33] font-semibold">
            <BookOpen size={12} /> What this course covered
          </div>
          <h3 className="mt-2 font-display text-[18px] tracking-tight text-slate-900 font-semibold">
            {module.title || competencyName}
          </h3>
          <p className="mt-2 text-[13.5px] text-slate-700 leading-relaxed">
            {module.summary}
          </p>
        </section>
      )}

      {/* Key takeaways / compliance reminders */}
      {aggregatedTakeaways.length > 0 && (
        <section
          className="rounded-xl border border-[#D4A65A]/40 bg-[#FDF6E7] p-5"
          data-testid="completion-key-takeaways"
        >
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-[#8C6B33] font-semibold">
            <Lightbulb size={12} /> Compliance reminders
          </div>
          <p className="mt-1 text-[12.5px] text-slate-600">
            Carry these into your day-to-day practice.
          </p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-2">
            {aggregatedTakeaways.map((t, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-[13px] text-slate-800 leading-relaxed"
              >
                <CheckCircle2 size={13} className="text-[#8C6B33] mt-1 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Strengths */}
      {correctItems.length > 0 && (
        <section
          className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5"
          data-testid="completion-strengths"
        >
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-emerald-800 font-semibold">
            <TrendingUp size={12} /> Strengths demonstrated
          </div>
          <p className="mt-1 text-[12.5px] text-emerald-900/80">
            You answered {correctItems.length} of {perQuestion.length} question
            {perQuestion.length === 1 ? "" : "s"} correctly.
          </p>
          <ul className="mt-3 space-y-2">
            {correctItems.map((r) => {
              const q = questionById[r.question_id];
              return (
                <li
                  key={r.question_id}
                  className="flex items-start gap-2 text-[13px] text-slate-800 leading-relaxed"
                >
                  <CheckCircle2
                    size={13}
                    className="text-emerald-600 mt-1 shrink-0"
                  />
                  <span className="line-clamp-2">
                    {q?.prompt || "Correctly answered a question in this module."}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Areas to review — incorrect answers with explanations */}
      {incorrectItems.length > 0 && (
        <section
          className="rounded-xl border border-rose-200 bg-rose-50/40 p-5"
          data-testid="completion-review"
        >
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] text-rose-800 font-semibold">
            <XCircle size={12} /> Areas to review
          </div>
          <p className="mt-1 text-[12.5px] text-rose-900/80">
            {incorrectItems.length} question{incorrectItems.length === 1 ? "" : "s"} to revisit.
            Read the explanations — these are the compliance points to reinforce.
          </p>
          <div className="mt-3 space-y-3" data-testid="assessment-review">
            {incorrectItems.map((r) => {
              const q = questionById[r.question_id];
              const options = q?.options || [];
              const pickedOpt = options.find((o) => o.key === r.picked);
              const correctOpt = options.find((o) => o.key === r.correct_key);
              return (
                <div
                  key={r.question_id}
                  className="rounded-lg border border-rose-200 bg-white p-4"
                  data-testid={`review-question-${r.question_id}`}
                >
                  {q?.prompt && (
                    <div className="text-[13px] text-slate-900 font-medium leading-relaxed">
                      {q.prompt}
                    </div>
                  )}
                  <div className="mt-2 space-y-1 text-[12.5px]">
                    <div className="flex items-start gap-2">
                      <XCircle size={12} className="text-rose-600 mt-1 shrink-0" />
                      <span className="text-rose-800">
                        <b className="mr-1">Your answer:</b>
                        {r.picked ? (
                          <>
                            {r.picked}
                            {pickedOpt ? ` — ${pickedOpt.text}` : ""}
                          </>
                        ) : (
                          "No answer"
                        )}
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2
                        size={12}
                        className="text-emerald-600 mt-1 shrink-0"
                      />
                      <span className="text-emerald-800">
                        <b className="mr-1">Correct answer:</b>
                        {r.correct_key}
                        {correctOpt ? ` — ${correctOpt.text}` : ""}
                      </span>
                    </div>
                  </div>
                  {r.explanation && (
                    <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-3 text-[12.5px] text-slate-700 leading-relaxed flex items-start gap-2">
                      <Lightbulb size={12} className="text-[#8C6B33] mt-0.5 shrink-0" />
                      <span>
                        <b className="text-slate-900">Why this matters: </b>
                        {r.explanation}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Certificate CTA (pass only) */}
      {passed && (
        <section
          className="rounded-2xl border border-[#D4A65A]/50 bg-gradient-to-br from-[#FDF6E7] via-white to-white p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
          data-testid="completion-cert-cta"
        >
          <span className="h-12 w-12 rounded-xl bg-[#0B1220] text-[#D4A65A] flex items-center justify-center shrink-0">
            <Award size={20} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#8C6B33] font-semibold">
              Your certificate
            </div>
            <div className="mt-1 font-display text-[17px] tracking-tight text-slate-900 font-semibold">
              {certificate?.cert_id ? (
                <>
                  Certificate <span className="font-mono">{certificate.cert_id}</span> is ready.
                </>
              ) : (
                "Your certificate is ready to view."
              )}
            </div>
            <p className="mt-1 text-[12.5px] text-slate-600">
              Download the A4 PDF, print it, or share the verification link with your compliance manager.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, children, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3.5">
      <dt className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">
        {icon}
        {label}
      </dt>
      <dd
        className="mt-1.5 text-[13.5px] text-slate-900 font-semibold"
        data-testid={testid}
      >
        {children}
      </dd>
    </div>
  );
}

/* ------------ SOP viewer modal (backup access from learn stage) ------------ */

function SopViewerModal({ url, title, version, onClose }) {
  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/70 flex items-stretch md:items-center justify-center p-0 md:p-4"
      data-testid="sop-viewer-modal"
    >
      <div className="w-full md:max-w-4xl md:h-[88vh] bg-white md:rounded-2xl shadow-xl border border-slate-200 flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
              Original source document
            </div>
            <div className="mt-1 font-display text-lg tracking-tight text-slate-900 font-semibold truncate">
              {title}
              {version && (
                <span className="text-slate-500 font-normal"> · v{version}</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="sop-viewer-close"
            className="text-slate-400 hover:text-slate-700 shrink-0"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-hidden bg-slate-50">
          <iframe
            src={url}
            title={title || "Source document"}
            className="w-full h-full"
            data-testid="sop-viewer-frame"
          />
        </div>
      </div>
    </div>
  );
}

/* ------------ Certificate modal ------------ */

function CertificateModal({ cert, onClose }) {
  const [downloading, setDownloading] = useState(false);

  const certProps = {
    staffName: cert.team_member_name,
    organisation: cert.business_name,
    competencyTitle: cert.module,
    score: cert.score != null ? `${cert.score}%` : "\u2014",
    passMark: cert.pass_mark ? `${cert.pass_mark}%` : "80%",
    completedOn: cert.completed_at
      ? new Date(cert.completed_at).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      : "",
    // Certificates are stored without valid_until (the API returns it empty),
    // so every issued certificate printed a dash for the expiry. Derive it from
    // the issue date instead - 12 months, matching the dossier's default
    // certificate_validity_months.
    validUntil: (() => {
      const src = cert.valid_until || cert.completed_at;
      if (!src) return "";
      const d = new Date(src);
      if (Number.isNaN(d.getTime())) return "";
      if (!cert.valid_until) d.setMonth(d.getMonth() + 12);
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    })(),
    certId: cert.cert_id,
    staffRole: cert.team_member_role,
    version: cert.version,
  };

  async function download() {
    setDownloading(true);
    try {
      const safeName = (cert.team_member_name || "certificate")
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase();
      await exportCertificatePDF(certProps, `${safeName}-${cert.cert_id}.pdf`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-start justify-center overflow-y-auto p-3 sm:p-4">
      <div className="w-full max-w-5xl relative my-4 sm:my-6">
        <div className="flex items-center justify-end mb-3 gap-2">
          <button
            onClick={download}
            disabled={downloading}
            data-testid="my-cert-download"
            className="inline-flex items-center gap-1.5 bg-[#D4A65A] hover:bg-[#E6BE7A] disabled:opacity-40 text-[#0B1220] rounded-lg px-3 py-1.5 text-[12px] font-semibold"
          >
            <Download size={14} /> {downloading ? "Building PDF…" : "Download PDF"}
          </button>
          <button
            onClick={onClose}
            data-testid="cert-modal-close"
            className="text-white/80 hover:text-white text-[12px] font-semibold inline-flex items-center gap-1"
          >
            <X size={14} /> Close
          </button>
        </div>
        {/* Mobile: certificate keeps its full readable width via min-width, and
            the outer scroller lets the user pan/pinch. Desktop: fits the modal. */}
        <div className="rounded-xl bg-white shadow-2xl overflow-x-auto">
          <div className="min-w-[720px]">
            <ScaledCertificate {...certProps} />
          </div>
        </div>
      </div>
    </div>
  );
}
